import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Package, RefreshCw, Search, Copy, Store, ShieldAlert, FilePlus2, Pencil, Trash2, CheckCircle2, AlertTriangle, Layers, Boxes, History, ChevronDown, Lock, Unlock, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PLATFORMS = [["", "All platforms"], ["shopee", "Shopee"], ["tiktok", "TikTok Shop"]];
const STATUS = [["", "All"], ["live", "Live"], ["draft_platform", "Platform Draft"], ["deactivated", "Deactivated"], ["other", "Other"]];
const BUCKET_LABEL = { live: "Live", draft_platform: "Platform Draft", deactivated: "Deactivated", other: "Other" };
const MAP_STYLE = {
  mapped: "border-emerald-300 text-emerald-700 bg-emerald-50",
  unmapped: "border-slate-300 text-slate-500 bg-slate-50",
  conflict: "border-amber-300 text-amber-800 bg-amber-50",
  invalid: "border-rose-300 text-rose-700 bg-rose-50",
};
const CURRENCIES = ["MYR", "SGD", "IDR", "THB", "PHP", "VND", "USD", "TWD", "BRL"];

export default function MarketplaceProducts() {
  const { can } = useAuth();
  const [tab, setTab] = useState("listings");
  const [platform, setPlatform] = useState("");
  const [shopId, setShopId] = useState("");
  const [stores, setStores] = useState([]);
  const showOutbound = can("marketplace:outbound_review");

  const filtered = stores.filter((s) => !platform || s.platform === platform);

  const TABS = [["listings", "Listings"], ["drafts", "Drafts"]];
  if (showOutbound) TABS.push(["outbound", "Outbound (Dry-Run)"]);

  return (
    <div className="space-y-4" data-testid="marketplace-products-page">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-400">Marketplace</div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="w-6 h-6 text-indigo-500" />Products</h1>
        <p className="text-sm text-slate-500 mt-0.5">Read-only listing imports, and reviewable drafts that reuse your Master SKU / Combo mapping. No live publishing — all external writes are blocked in this phase.</p>
      </div>

      <div className="flex gap-1.5 flex-wrap" data-testid="products-tabs">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`products-tab-${v}`} onClick={() => setTab(v)}
            className={`px-4 h-9 rounded-md text-sm border ${tab === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {tab !== "outbound" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Platform</label>
            <select data-testid="prod-platform" value={platform} onChange={(e) => { setPlatform(e.target.value); setShopId(""); }} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
              {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Store</label>
            <select data-testid="prod-store" value={shopId} onChange={(e) => setShopId(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
              <option value="">All connected stores{platform ? "" : " (choose platform)"}</option>
              {filtered.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>
      )}

      {tab === "listings" && <ListingsTab platform={platform} shopId={shopId} onStores={setStores} />}
      {tab === "drafts" && <DraftsTab platform={platform} shopId={shopId} stores={stores} onStores={setStores} />}
      {tab === "outbound" && <OutboundTab />}
    </div>
  );
}

// Shows a single imported variant. Per-warehouse stock (raw, never summed) is revealed on demand.
// Legacy single-value stock semantics differ by platform and are labelled honestly:
//  - TikTok: `stock` is the FIRST warehouse only (not a total).
//  - Shopee: `stock` is the store available-stock summary; available vs reserved kept distinct.
function VariantRow({ v, platform, idx }) {
  const [open, setOpen] = useState(false);
  const whs = Array.isArray(v.warehouses) ? v.warehouses : [];
  const key = v.seller_sku || idx;
  const show = (q) => (q === null || q === undefined ? "unknown" : q); // 0 stays visible
  return (
    <div className="bg-slate-50 rounded-md px-2 py-1 text-[11px]" data-testid={`listing-variant-${key}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-mono font-semibold">{v.seller_sku || "—"}</span>
        {v.price != null && <span className="text-slate-500">{v.currency} {v.price}</span>}
        {platform === "tiktok"
          ? <span className="text-slate-400" data-testid={`variant-stock-${key}`}>stock {v.stock ?? "—"} <span className="text-slate-300">(first warehouse)</span></span>
          : <>
              <span className="text-slate-400" data-testid={`variant-available-${key}`}>available {show(v.available_stock ?? v.stock)}</span>
              <span className="text-slate-400" data-testid={`variant-reserved-${key}`}>reserved {show(v.reserved_stock)}</span>
              <span className="text-slate-300">(store summary)</span>
            </>}
        <MappingBadge m={v.mapping} />
        {whs.length > 0 && (
          <button data-testid={`variant-warehouses-toggle-${key}`} onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-0.5 text-indigo-600 hover:text-indigo-800">
            <Boxes className="w-3 h-3" />{whs.length} warehouse{whs.length > 1 ? "s" : ""}
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {open && whs.length > 0 && (
        <div className="mt-1 ml-1 border-l-2 border-indigo-100 pl-2 space-y-0.5" data-testid={`variant-warehouses-${key}`}>
          {whs.map((w, k) => (
            <div key={k} className="flex items-center gap-2 text-slate-500" data-testid={`warehouse-row-${key}-${k}`}>
              <span className="font-mono text-slate-600">{w.warehouse_id || "unknown"}</span>
              <span>qty {show(w.quantity)}</span>
              {w.stock_type && <Badge variant="outline" className="text-[9px] px-1 py-0 border-slate-300 text-slate-500">{w.stock_type}</Badge>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MappingBadge({ m }) {
  if (!m) return null;
  return (
    <Badge variant="outline" className={`text-[10px] ${MAP_STYLE[m.state] || MAP_STYLE.unmapped}`} data-testid={`variant-map-state`}>
      {m.state === "mapped" ? `→ ${m.target_code}` : m.state}
    </Badge>
  );
}

const IMPORT_MODES = [
  ["full", "Full catalog"],
  ["new", "New listings only"],
  ["targeted", "Specific Item IDs"],
  ["resume", "Resume last import"],
];
const MODE_HINT = {
  full: "Fresh sweep to true provider exhaustion — refreshes existing listings and adds new ones. Resumable if interrupted.",
  new: "Discovery only — imports listings that are missing from your catalog. Never overwrites an existing row or its drafts.",
  targeted: "Re-pull only the Item IDs you paste. Does not touch the full-sweep cursor or coverage claim.",
  resume: "Continue the last interrupted Full/New sweep from its saved cursor — no restart, no re-truncation.",
};

// Honest, persisted, store+mode-scoped catalog-import status (pages/counts/coverage/partial/resumable/errors).
function CatalogStatusLine({ cat, onRefresh }) {
  const s = cat?.status || "idle";
  const chip = (label, cls) => <Badge variant="outline" className={`text-[10px] ${cls}`}>{label}</Badge>;
  if (s === "idle") {
    return (
      <div className="text-xs text-slate-400 flex items-center gap-2" data-testid="catalog-status">
        No import has run for this store yet.
        <button data-testid="catalog-status-refresh" onClick={onRefresh} className="text-indigo-600 hover:underline">Refresh</button>
      </div>
    );
  }
  const cov = cat.coverage_complete;
  return (
    <div className="text-xs text-slate-600 flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="catalog-status">
      <span className="font-medium capitalize" data-testid="catalog-status-state">{s}</span>
      {cat.mode && <span className="text-slate-400">· mode {cat.mode}</span>}
      <span className="text-slate-400">· {cat.pages_done ?? 0} page(s)</span>
      <span className="text-slate-400">· {cat.listings_seen ?? 0} seen</span>
      <span className="text-emerald-700">+{cat.inserted ?? 0} new</span>
      <span className="text-slate-500">{cat.updated ?? 0} refreshed</span>
      {cat.skipped ? <span className="text-slate-500">{cat.skipped} kept</span> : null}
      {cov === true ? chip("coverage complete", "border-emerald-300 text-emerald-700 bg-emerald-50")
        : cov === false ? chip("coverage incomplete", "border-amber-300 text-amber-800 bg-amber-50")
        : chip("coverage unknown", "border-slate-300 text-slate-500 bg-slate-50")}
      {cat.resumable && chip("resumable", "border-indigo-300 text-indigo-700 bg-indigo-50")}
      {cat.has_errors && chip(`${(cat.errors || []).length} error(s)`, "border-rose-300 text-rose-700 bg-rose-50")}
      <button data-testid="catalog-status-refresh" onClick={onRefresh} className="text-indigo-600 hover:underline ml-1">Refresh</button>
    </div>
  );
}

function ListingsTab({ platform, shopId, onStores }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("full");
  const [itemIds, setItemIds] = useState("");
  const [importing, setImporting] = useState(false);
  const [cat, setCat] = useState(null);
  const storeChosen = Boolean(platform && shopId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ platform, shop_id: shopId, status, q }).toString();
      const r = await api.get(`/marketplace/products?${params}`);
      setData(r.data);
      onStores(r.data.stores || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [platform, shopId, status, q, onStores]);

  const loadStatus = useCallback(async () => {
    if (!platform || !shopId) { setCat(null); return; }
    try {
      const params = new URLSearchParams({ platform, shop_id: shopId }).toString();
      const r = await api.get(`/marketplace/products/catalog/status?${params}`);
      setCat(r.data);
    } catch { setCat(null); }
  }, [platform, shopId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [platform, shopId, status]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runImport = async () => {
    if (!storeChosen) return toast.error("Choose a platform and a specific store to import");
    const ids = itemIds.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    if (mode === "targeted" && ids.length === 0) return toast.error("Paste one or more Item IDs for a targeted import");
    setImporting(true);
    try {
      const body = { platform, shop_id: shopId, mode };
      if (mode === "targeted") body.item_ids = ids;
      const r = await api.post("/marketplace/products/catalog/import", body);
      const d = r.data || {};
      if (d.status === "busy") toast.message("An import is already running for this store — try again shortly.");
      else if (d.status === "nothing_to_resume") toast.message("No partial import to resume for this store.");
      else if (d.status === "failed") toast.error(`Import failed${d.error ? ` — ${d.error}` : ""}`);
      else if (d.mode === "targeted") toast.success(`Targeted import ${d.status} — +${d.inserted ?? 0} new, ${d.updated ?? 0} refreshed`);
      else toast.success(`Import ${d.status} — +${d.inserted ?? 0} new, ${d.updated ?? 0} refreshed${d.skipped_existing ? `, ${d.skipped_existing} kept` : ""}`);
      await Promise.all([load(), loadStatus()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const rows = data?.items || [];
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="catalog-import-panel">
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Import</label>
            <select data-testid="import-mode" value={mode} onChange={(e) => setMode(e.target.value)} disabled={importing} className="h-9 rounded-md border border-slate-200 text-sm px-2">
              {IMPORT_MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <Button data-testid="catalog-import-run" onClick={runImport} disabled={importing || !storeChosen} className="h-9 bg-indigo-600 hover:bg-indigo-700">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${importing ? "animate-spin" : ""}`} />{importing ? "Importing…" : "Run import"}
          </Button>
          {!storeChosen && <span className="text-xs text-amber-600" data-testid="import-need-store">Choose a platform and a specific store to import.</span>}
        </div>
        {mode === "targeted" && (
          <textarea data-testid="import-item-ids" value={itemIds} onChange={(e) => setItemIds(e.target.value)} rows={2} disabled={importing}
            placeholder="Paste Item IDs (comma, space or newline separated)" className="w-full rounded-md border border-slate-200 text-sm p-2 font-mono" />
        )}
        <p className="text-[11px] text-slate-500" data-testid="import-mode-hint">{MODE_HINT[mode]}</p>
        <CatalogStatusLine cat={cat || { status: "idle" }} onRefresh={loadStatus} />
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="text-xs text-slate-500 block mb-1">Status</label>
          <select data-testid="listing-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-slate-200 text-sm px-2">
            {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input data-testid="listing-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search title / SKU" className="pl-8 h-9 w-56" />
        </div>
      </div>

      {loading ? <div className="p-8 text-center text-slate-400">Loading…</div> : (
        <div className="space-y-2">
          {rows.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="listing-empty">No listings imported for this filter. Choose a connected store and run an import above.</div> :
            rows.map((it) => (
              <div key={it.id} data-testid={`listing-row-${it.listing_id}`} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{it.title || "(untitled)"}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                      <span className="capitalize">{it.platform}</span>·<span>{it.store}</span>·<span className="font-mono">{it.listing_id}</span>
                      <Badge variant="outline" className="text-[10px]">{BUCKET_LABEL[it.status_bucket] || it.status_bucket}</Badge>
                    </div>
                  </div>
                  <div className="text-right text-[11px] text-slate-500 whitespace-nowrap">
                    <div>{it.mapped_count}/{it.variant_count} mapped</div>
                    <CreateDraftButton listing={it} />
                  </div>
                </div>
                <div className="mt-2 flex flex-col gap-1.5">
                  {it.variants.map((v, i) => (
                    <VariantRow key={i} v={v} platform={it.platform} idx={i} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function CreateDraftButton({ listing }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/marketplace/drafts", {
        platform: listing.platform, shop_id: listing.shop_id,
        source: { type: "listing", listing_pk: listing.id },
      });
      toast.success("Draft created from listing (mappings reused). Open the Drafts tab to review.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return <Button size="sm" variant="outline" className="h-7 text-xs mt-1" disabled={busy} data-testid={`listing-create-draft-${listing.listing_id}`} onClick={go}><FilePlus2 className="w-3 h-3 mr-1" />Create draft</Button>;
}

function DraftsTab({ platform, shopId, stores, onStores }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  const [copyDraft, setCopyDraft] = useState(null);
  const [pubDraft, setPubDraft] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ platform, shop_id: shopId }).toString();
      const [d, w] = await Promise.all([
        api.get(`/marketplace/drafts?${params}`),
        api.get(`/marketplace/products?${new URLSearchParams({ platform }).toString()}`),
      ]);
      setData(d.data);
      onStores(w.data.stores || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [platform, shopId, onStores]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [platform, shopId]);

  const del = async (d) => {
    if (!window.confirm(`Delete draft "${d.title}"? This is audited.`)) return;
    try { await api.delete(`/marketplace/drafts/${d.id}`); toast.success("Draft deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dup = async (d) => {
    try { await api.post(`/marketplace/drafts/${d.id}/duplicate`); toast.success("Draft duplicated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = data?.items || [];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button data-testid="draft-new-btn" onClick={() => setNewOpen(true)} className="h-9 bg-emerald-600 hover:bg-emerald-700"><FilePlus2 className="w-4 h-4 mr-1.5" />New draft</Button>
      </div>
      {loading ? <div className="p-8 text-center text-slate-400">Loading…</div> : (
        <div className="space-y-2">
          {rows.length === 0 ? <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="draft-empty">No drafts yet. Create one from a listing or start a new draft.</div> :
            rows.map((d) => (
              <div key={d.id} data-testid={`draft-row-${d.id}`} className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{d.title || "(untitled draft)"}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                      <span className="capitalize">{d.platform}</span>·<span>{d.store}</span>·<span>rev {d.revision}</span>·<span>{d.variants?.length || 0} variant(s)</span>
                    </div>
                  </div>
                  <div>
                    {d.review_status === "ready"
                      ? <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50" data-testid={`draft-status-${d.id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Ready</Badge>
                      : <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800 bg-amber-50" data-testid={`draft-status-${d.id}`}><AlertTriangle className="w-3 h-3 mr-1" />{d.validation?.error_count || 0} issue(s)</Badge>}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`draft-edit-${d.id}`} onClick={() => setEditDraft(d)}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`draft-dup-${d.id}`} onClick={() => dup(d)}><Copy className="w-3 h-3 mr-1" />Duplicate</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`draft-copy-${d.id}`} onClick={() => setCopyDraft(d)}><Store className="w-3 h-3 mr-1" />Copy to store</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-700 border-indigo-200" data-testid={`draft-publish-${d.id}`} onClick={() => setPubDraft(d)}><ShieldAlert className="w-3 h-3 mr-1" />Publish</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200" data-testid={`draft-del-${d.id}`} onClick={() => del(d)}><Trash2 className="w-3 h-3" /></Button>
                </div>
                {d.review_status !== "ready" && (d.validation?.issues?.length > 0) && (
                  <ul className="mt-2 text-[11px] text-amber-700 list-disc pl-4 space-y-0.5" data-testid={`draft-issues-${d.id}`}>
                    {d.validation.issues.slice(0, 8).map((is, i) => <li key={i}>{is.message}</li>)}
                  </ul>
                )}
              </div>
            ))}
        </div>
      )}
      {newOpen && <DraftDialog stores={stores} onClose={() => setNewOpen(false)} onSaved={() => { setNewOpen(false); load(); }} />}
      {editDraft && <DraftDialog draft={editDraft} stores={stores} onClose={() => setEditDraft(null)} onSaved={() => { setEditDraft(null); load(); }} />}
      {copyDraft && <CopyDialog draft={copyDraft} stores={stores} onClose={() => setCopyDraft(null)} onSaved={() => { setCopyDraft(null); load(); }} />}
      {pubDraft && <PublishDialog draft={pubDraft} onClose={() => setPubDraft(null)} />}
    </div>
  );
}

const emptyVariant = () => ({ seller_sku: "", name: "", price: "", currency: "MYR", stock: 0, warehouse_id: "", master_sku_mapping: null });

function DraftDialog({ draft, stores, onClose, onSaved }) {
  const isEdit = !!draft;
  const [platform, setPlatform] = useState(draft?.platform || "shopee");
  const [shopId, setShopId] = useState(draft?.shop_id || "");
  const [title, setTitle] = useState(draft?.title || "");
  const [categoryId, setCategoryId] = useState(draft?.category_id || "");
  const [variants, setVariants] = useState(draft?.variants?.length ? draft.variants.map(v => ({ ...v, price: v.price ?? "" })) : [emptyVariant()]);
  const [saving, setSaving] = useState(false);
  const [mapFor, setMapFor] = useState(null);

  const storeOpts = stores.filter((s) => s.platform === platform);
  const setV = (i, patch) => setVariants((vs) => vs.map((v, k) => k === i ? { ...v, ...patch } : v));

  const save = async () => {
    if (!isEdit && !shopId) return toast.error("Choose a target store");
    setSaving(true);
    try {
      const body = {
        title, category_id: categoryId,
        variants: variants.map((v) => ({
          variation_id: v.variation_id || "", seller_sku: v.seller_sku, name: v.name,
          price: v.price === "" ? null : Number(v.price), currency: v.currency,
          stock: v.stock === "" ? null : Number(v.stock), warehouse_id: v.warehouse_id || "",
          master_sku_mapping: v.master_sku_mapping || null,
        })),
      };
      if (isEdit) {
        await api.put(`/marketplace/drafts/${draft.id}`, body);
      } else {
        await api.post("/marketplace/drafts", { platform, shop_id: shopId, source: { type: "manual" }, ...body });
      }
      toast.success(isEdit ? "Draft updated (new revision saved)" : "Draft created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="draft-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit draft" : "New draft"}</DialogTitle>
          <DialogDescription>Drafts reuse your Master SKU / Combo mapping. Category is validated per target platform — nothing is published.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Platform</label>
                <select data-testid="draft-platform" value={platform} onChange={(e) => { setPlatform(e.target.value); setShopId(""); }} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
                  <option value="shopee">Shopee</option><option value="tiktok">TikTok Shop</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Store</label>
                <select data-testid="draft-store" value={shopId} onChange={(e) => setShopId(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
                  <option value="">Select connected store</option>
                  {storeOpts.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500 block mb-1">Title</label>
            <Input data-testid="draft-title" value={title} onChange={(e) => setTitle(e.target.value)} className="h-9" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Target category ID ({platform === "tiktok" ? "TikTok" : "Shopee"})</label>
            <Input data-testid="draft-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} placeholder="Platform category id" className="h-9" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs text-slate-500">Variants</label>
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="draft-add-variant" onClick={() => setVariants((vs) => [...vs, emptyVariant()])}>+ Add variant</Button>
            </div>
            {variants.map((v, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2.5 space-y-2" data-testid={`draft-variant-${i}`}>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid={`draft-v-sku-${i}`} value={v.seller_sku} onChange={(e) => setV(i, { seller_sku: e.target.value, master_sku_mapping: null })} placeholder="Seller SKU" className="h-8 text-xs font-mono" />
                  <Input data-testid={`draft-v-name-${i}`} value={v.name} onChange={(e) => setV(i, { name: e.target.value })} placeholder="Variant name" className="h-8 text-xs" />
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <Input data-testid={`draft-v-price-${i}`} value={v.price} onChange={(e) => setV(i, { price: e.target.value })} placeholder="Price" type="number" className="h-8 text-xs" />
                  <select data-testid={`draft-v-cur-${i}`} value={v.currency} onChange={(e) => setV(i, { currency: e.target.value })} className="h-8 rounded-md border border-slate-200 text-xs px-1">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Input data-testid={`draft-v-stock-${i}`} value={v.stock} onChange={(e) => setV(i, { stock: e.target.value })} placeholder="Stock" type="number" className="h-8 text-xs" />
                  {platform === "tiktok"
                    ? <Input data-testid={`draft-v-wh-${i}`} value={v.warehouse_id} onChange={(e) => setV(i, { warehouse_id: e.target.value })} placeholder="Warehouse id" className="h-8 text-xs" />
                    : <div className="text-[10px] text-slate-300 flex items-center">n/a</div>}
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[11px]">
                    {v.master_sku_mapping?.target_id
                      ? <span className="text-emerald-700 flex items-center gap-1">{v.master_sku_mapping.target_type === "combo" ? <Boxes className="w-3 h-3" /> : <Layers className="w-3 h-3" />}mapped → {v.master_sku_mapping.target_code}</span>
                      : <span className="text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />no Master SKU / Combo mapping</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`draft-v-map-${i}`} onClick={() => setMapFor(i)}>Map SKU</Button>
                    {variants.length > 1 && <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600" onClick={() => setVariants((vs) => vs.filter((_, k) => k !== i))}>Remove</Button>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="h-9">Cancel</Button>
            <Button data-testid="draft-save-btn" onClick={save} disabled={saving} className="h-9 bg-emerald-600 hover:bg-emerald-700">{saving ? "Saving…" : isEdit ? "Save (new revision)" : "Create draft"}</Button>
          </div>
        </div>
        {mapFor != null && <MapPicker onPick={(m) => { setV(mapFor, { master_sku_mapping: m }); setMapFor(null); }} onClose={() => setMapFor(null)} />}
      </DialogContent>
    </Dialog>
  );
}

function MapPicker({ onPick, onClose }) {
  const [ttype, setTtype] = useState("master");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const search = async () => {
    try { const r = await api.get(`/marketplace/products/search-targets?type=${ttype}&q=${encodeURIComponent(q)}`); setResults(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="draft-map-picker">
        <DialogHeader><DialogTitle>Map to Master SKU / Combo</DialogTitle><DialogDescription>Reuses the same targets as Phase 1 SKU Mapping.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <div className="flex gap-1.5">
            {[["master", "Master SKU"], ["combo", "Combo"]].map(([v, l]) => (
              <button key={v} data-testid={`mp-ttype-${v}`} onClick={() => { setTtype(v); setResults([]); }} className={`px-3 h-8 rounded-md text-xs border ${ttype === v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>{l}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <Input data-testid="mp-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search code / name" className="h-9" />
            <Button data-testid="mp-search-btn" onClick={search} className="h-9 bg-slate-900"><Search className="w-4 h-4" /></Button>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y border rounded-md" data-testid="mp-results">
            {results.length === 0 ? <div className="p-3 text-xs text-slate-400">Search a target</div> :
              results.map((r) => (
                <button key={r.id} data-testid={`mp-result-${r.code}`} onClick={() => onPick({ target_type: ttype, target_id: r.id, target_code: r.code })} className="w-full text-left p-2 text-xs hover:bg-slate-50 flex gap-2">
                  <span className="font-mono font-semibold">{r.code}</span><span className="text-slate-500 truncate">{r.name}</span>
                </button>
              ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CopyDialog({ draft, stores, onClose, onSaved }) {
  const [tp, setTp] = useState(draft.platform);
  const [ts, setTs] = useState("");
  const [busy, setBusy] = useState(false);
  const opts = stores.filter((s) => s.platform === tp && !(s.platform === draft.platform && s.value === draft.shop_id));
  const go = async () => {
    if (!ts) return toast.error("Choose a target store");
    setBusy(true);
    try {
      const r = await api.post(`/marketplace/drafts/${draft.id}/copy-to-store`, { target_platform: tp, target_shop_id: ts });
      toast.success(r.data.source?.cross_platform ? "Copied — category cleared for the new platform, re-validate." : "Copied to store");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="draft-copy-dialog">
        <DialogHeader><DialogTitle>Copy draft to another store</DialogTitle><DialogDescription>Warehouse is cleared (store-specific). Across platforms, category/attributes are cleared — they never copy blindly.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <select data-testid="copy-platform" value={tp} onChange={(e) => { setTp(e.target.value); setTs(""); }} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
            <option value="shopee">Shopee</option><option value="tiktok">TikTok Shop</option>
          </select>
          <select data-testid="copy-store" value={ts} onChange={(e) => setTs(e.target.value)} className="w-full h-9 rounded-md border border-slate-200 text-sm px-2">
            <option value="">Select target store</option>
            {opts.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose} className="h-9">Cancel</Button><Button data-testid="copy-go" onClick={go} disabled={busy} className="h-9 bg-slate-900">{busy ? "Copying…" : "Copy"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PublishDialog({ draft, onClose }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.post(`/marketplace/drafts/${draft.id}/publish`); setRes(r.data); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [draft]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="draft-publish-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-500" />Publish is blocked</DialogTitle><DialogDescription>Phase 2 does not perform live writes.</DialogDescription></DialogHeader>
        {res == null ? <div className="p-4 text-sm text-slate-400">Checking…</div> : (
          <div className="space-y-3">
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3" data-testid="publish-reason">{res.reason}</div>
            <div className="text-xs text-slate-600">Validation: {res.validation_passed ? <span className="text-emerald-700 font-semibold">passed</span> : <span className="text-rose-600 font-semibold">has errors</span>}</div>
            {!res.validation_passed && res.issues?.length > 0 && (
              <ul className="text-[11px] text-rose-700 list-disc pl-4 space-y-0.5" data-testid="publish-issues">
                {res.issues.slice(0, 10).map((is, i) => <li key={i}>{is.message}</li>)}
              </ul>
            )}
            {res.would_send && (
              <div>
                <div className="text-xs text-slate-500 mb-1">Payload a future live phase would send (not sent):</div>
                <pre className="text-[10px] bg-slate-900 text-slate-100 rounded-md p-2 overflow-x-auto max-h-56" data-testid="publish-preview">{JSON.stringify(res.would_send, null, 2)}</pre>
              </div>
            )}
            <div className="flex justify-end"><Button variant="outline" onClick={onClose} className="h-9">Close</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// Dry-run outbound review. ZERO live writes: every control shows the diff/plan a future live phase
// WOULD send, gated by Master + per-capability + grant + store/RBAC checks (fail-closed, dev-locked).
function GateChip({ ok, label }) {
  return (
    <span data-testid={`gate-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${ok ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-slate-300 text-slate-500 bg-slate-50"}`}>
      {ok ? <Unlock className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}{label}
    </span>
  );
}

function OutboundTab() {
  const [manifest, setManifest] = useState(null);
  const [elig, setElig] = useState(null);
  const [stores, setStores] = useState([]);
  const [sel, setSel] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [m, e, p] = await Promise.all([
          api.get("/marketplace/outbound/manifest"),
          api.get("/marketplace/outbound/eligibility"),
          api.get("/marketplace/products"),
        ]);
        setManifest(m.data); setElig(e.data); setStores(p.data.stores || []);
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-400">Loading…</div>;
  const store = stores.find((s) => `${s.platform}:${s.value}` === sel);

  return (
    <div className="space-y-4" data-testid="outbound-tab">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="outbound-safety-banner">
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 space-y-1">
            <div className="font-semibold text-sm">Dry-run only — no live marketplace write is ever sent in this build.</div>
            <div>Every control below computes the diff/payload a future live phase <b>would</b> transmit, then stops. A real write needs ALL gates true <i>and</i> removal of the develop-only lock.</div>
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="outbound-master-gates">
              <GateChip ok={manifest.master_enabled} label="Master gate" />
              <GateChip ok={!manifest.dev_no_live_write_lock} label="Dev write lock lifted" />
              {Object.entries(manifest.capability_flags || {}).map(([c, on]) => <GateChip key={c} ok={on} label={c} />)}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="outbound-manifest">
        <div className="text-sm font-semibold text-slate-700">Capability contract & grant inventory</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {Object.entries(manifest.capabilities || {}).map(([cap, meta]) => (
            <div key={cap} className="rounded-lg border border-slate-200 p-2.5 text-xs" data-testid={`outbound-cap-${cap}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold capitalize">{meta.display || cap}</span>
                {meta.status && <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500">{meta.status}</Badge>}
              </div>
              <div className="text-slate-500 mt-1">{meta.policy || meta.reason}</div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {Object.entries(meta.platforms || {}).map(([pl, pm]) => (
                  <span key={pl} className="text-[10px] text-slate-400">{pl}: grant <b className="text-slate-600">{pm.grant_state || "n/a"}</b></span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="text-xs text-slate-500 block mb-1">Connected store (for Price / Stock review)</label>
        <select data-testid="outbound-store" value={sel} onChange={(e) => setSel(e.target.value)} className="w-full sm:w-96 h-9 rounded-md border border-slate-200 text-sm px-2">
          <option value="">Select a connected store</option>
          {stores.map((s) => <option key={`${s.platform}:${s.value}`} value={`${s.platform}:${s.value}`}>{s.label}</option>)}
        </select>
      </div>

      {store && <PriceReview store={store} />}
      {store && <StockReview store={store} />}

      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="outbound-eligibility">
        <div className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Megaphone className="w-4 h-4 text-indigo-500" />Ads & Affiliate eligibility (inventory only)</div>
        <p className="text-xs text-slate-500">Separate account/program grants. No account created, no spend, no terms accepted, no grant requested.</p>
        {Object.entries(elig || {}).map(([cap, meta]) => (
          <div key={cap} className="rounded-lg border border-slate-200 p-2.5 text-xs" data-testid={`eligibility-${cap}`}>
            <div className="font-semibold capitalize">{meta.display}</div>
            <div className="text-slate-500">{meta.reason}</div>
            <div className="flex flex-wrap gap-2 mt-1">
              {Object.entries(meta.platforms || {}).map(([pl, pm]) => (
                <span key={pl} className="text-[10px] text-slate-400">{pm.program}: grant <b className="text-slate-600">{pm.grant_state}</b></span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PriceReview({ store }) {
  const [form, setForm] = useState({ listing_id: "", variation_id: "", seller_sku: "", new_price: "", currency: "MYR" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/marketplace/outbound/price/plan", {
        platform: store.platform, shop_id: store.value,
        items: [{ ...form, new_price: form.new_price === "" ? null : Number(form.new_price) }],
      });
      setRes(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="outbound-price-review">
      <div className="text-sm font-semibold text-slate-700">Price — Review &amp; Update (dry-run)</div>
      <p className="text-xs text-slate-500">Deliberate per-variant intended price. Validated against the imported listing; shows old→new diff. Not submittable.</p>
      <div className="grid sm:grid-cols-5 gap-2">
        <Input data-testid="price-listing-id" value={form.listing_id} onChange={(e) => setForm({ ...form, listing_id: e.target.value })} placeholder="Listing ID" className="h-8 text-xs font-mono" />
        <Input data-testid="price-variation-id" value={form.variation_id} onChange={(e) => setForm({ ...form, variation_id: e.target.value })} placeholder="Variation ID" className="h-8 text-xs" />
        <Input data-testid="price-seller-sku" value={form.seller_sku} onChange={(e) => setForm({ ...form, seller_sku: e.target.value })} placeholder="Seller SKU" className="h-8 text-xs font-mono" />
        <Input data-testid="price-new-price" value={form.new_price} onChange={(e) => setForm({ ...form, new_price: e.target.value })} placeholder="New price" type="number" className="h-8 text-xs" />
        <select data-testid="price-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="h-8 rounded-md border border-slate-200 text-xs px-1">
          {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <Button data-testid="price-plan-btn" onClick={run} disabled={busy} className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700">{busy ? "Reviewing…" : "Review change"}</Button>
      {res && (
        <div className="mt-2 space-y-1" data-testid="price-plan-result">
          <div className="text-[11px] text-slate-500">{res.summary.valid} valid / {res.summary.invalid} invalid · <b>{res.submit_blocked_reason}</b></div>
          {res.rows.map((row, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-2 text-[11px]" data-testid={`price-row-${i}`}>
              <div className="flex flex-wrap gap-2 items-center">
                <span className="font-mono">{row.seller_sku || row.variation_id || row.listing_id}</span>
                <span className="text-slate-500">{row.currency} {row.old_price ?? "—"} → <b className="text-slate-800">{row.new_price ?? "—"}</b></span>
                {row.pct_change != null && <span className={row.pct_change >= 0 ? "text-emerald-600" : "text-rose-600"}>{row.pct_change > 0 ? "+" : ""}{row.pct_change}%</span>}
                {row.valid ? <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700 bg-emerald-50">valid</Badge>
                  : <Badge variant="outline" className="text-[9px] border-rose-300 text-rose-700 bg-rose-50">invalid</Badge>}
              </div>
              {row.issues?.length > 0 && <ul className="list-disc pl-4 text-rose-600 mt-0.5">{row.issues.map((is, k) => <li key={k}>{is}</li>)}</ul>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StockReview({ store }) {
  const [form, setForm] = useState({ listing_id: "", variation_id: "", seller_sku: "", intended_stock: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/marketplace/outbound/stock/plan", {
        platform: store.platform, shop_id: store.value,
        items: [{ ...form, intended_stock: form.intended_stock === "" ? null : Number(form.intended_stock) }],
      });
      setRes(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid="outbound-stock-review">
      <div className="text-sm font-semibold text-slate-700">Stock — Mapping review only</div>
      <p className="text-xs text-slate-500">PayRecon owns inventory. This ERP never deducts, reserves, restores or publishes marketplace stock — mapping review only until an owner-approved cutover.</p>
      <div className="grid sm:grid-cols-4 gap-2">
        <Input data-testid="stock-listing-id" value={form.listing_id} onChange={(e) => setForm({ ...form, listing_id: e.target.value })} placeholder="Listing ID" className="h-8 text-xs font-mono" />
        <Input data-testid="stock-variation-id" value={form.variation_id} onChange={(e) => setForm({ ...form, variation_id: e.target.value })} placeholder="Variation ID" className="h-8 text-xs" />
        <Input data-testid="stock-seller-sku" value={form.seller_sku} onChange={(e) => setForm({ ...form, seller_sku: e.target.value })} placeholder="Seller SKU" className="h-8 text-xs font-mono" />
        <Input data-testid="stock-intended" value={form.intended_stock} onChange={(e) => setForm({ ...form, intended_stock: e.target.value })} placeholder="Intended (review)" type="number" className="h-8 text-xs" />
      </div>
      <Button data-testid="stock-plan-btn" onClick={run} disabled={busy} className="h-8 text-xs bg-slate-800 hover:bg-slate-900">{busy ? "Reviewing…" : "Review mapping"}</Button>
      {res && (
        <div className="mt-2 space-y-1" data-testid="stock-plan-result">
          <div className="text-[11px] text-slate-500">{res.summary.found}/{res.summary.total} found · action always <b>none</b></div>
          {res.rows.map((row, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-2 text-[11px]" data-testid={`stock-row-${i}`}>
              <span className="font-mono">{row.seller_sku || row.variation_id || row.listing_id}</span>
              <span className="text-slate-500 ml-2">current {row.current_imported_stock ?? "—"} · intended {row.intended_stock ?? "—"}</span>
              <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-500 ml-2">action: none</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
